// src/components/PrivacyPolicy.jsx

import React, { useEffect } from "react";
import {
  ArrowLeft,
  CalendarDays,
  Database,
  ExternalLink,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import "../sass/pages/legal.scss";
import Logo from "../assets/logoClaro.webp";

const LAST_UPDATED = "13 de agosto de 2026";

const PrivacyPolicy = () => {
  useEffect(() => {
    const previousTitle = document.title;

    document.title =
      "Política de Privacidade | PsyAssist Pro";

    let description = document.querySelector(
      'meta[name="description"]'
    );

    const created = !description;

    if (!description) {
      description =
        document.createElement("meta");

      description.setAttribute(
        "name",
        "description"
      );

      document.head.appendChild(
        description
      );
    }

    const previousDescription =
      description.getAttribute("content");

    description.setAttribute(
      "content",
      "Política de Privacidade do PsyAssist Pro, incluindo tratamento de dados, LGPD e uso de dados do Google Calendar."
    );

    return () => {
      document.title = previousTitle;

      if (created) {
        description.remove();
      } else {
        description.setAttribute(
          "content",
          previousDescription || ""
        );
      }
    };
  }, []);

  return (
    <main className="psy-legal-page">
      <div className="psy-legal-page__ambient psy-legal-page__ambient--blue" />
      <div className="psy-legal-page__ambient psy-legal-page__ambient--violet" />

      <header className="psy-legal-nav">
        <a
          href="/"
          className="psy-legal-nav__brand"
          aria-label="Voltar para PsyAssist Pro"
        >
          <img src={Logo} alt="PsyAssist Pro" />

          <span>
            <strong>PsyAssist Pro</strong>
            <small>
              Privacidade e proteção de dados
            </small>
          </span>
        </a>

        <a
          href="/"
          className="psy-legal-nav__back"
        >
          <ArrowLeft size={16} />
          Voltar ao site
        </a>
      </header>

      <section className="psy-legal-hero">
        <div className="psy-legal-hero__eyebrow">
          <ShieldCheck size={15} />
          Transparência e proteção de dados
        </div>

        <h1>Política de Privacidade</h1>

        <p>
          Esta Política explica como o <strong>PsyAssist Pro</strong>{" "}
          coleta, utiliza, armazena, compartilha e protege dados pessoais,
          inclusive dados tratados por meio das integrações opcionais
          disponibilizadas na plataforma.
        </p>

        <div className="psy-legal-hero__meta">
          <span>Versão 1.0</span>
          <span>Atualizada em {LAST_UPDATED}</span>
          <span>Aplicável a psyassist.com.br</span>
        </div>
      </section>

      <div className="psy-legal-shell">
        <aside className="psy-legal-toc">
          <span>Nesta política</span>
          <a href="#quem-somos">1. Quem somos</a>
          <a href="#dados">2. Dados tratados</a>
          <a href="#finalidades">3. Finalidades</a>
          <a href="#google">4. Google Calendar</a>
          <a href="#compartilhamento">5. Compartilhamento</a>
          <a href="#retencao">6. Retenção</a>
          <a href="#seguranca">7. Segurança</a>
          <a href="#direitos">8. Seus direitos</a>
          <a href="#contato">9. Contato</a>
        </aside>

        <article className="psy-legal-content">
          <section
            id="quem-somos"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              01
            </span>

            <h2>
              Quem somos e a quem esta Política se aplica
            </h2>

            <p>
              O PsyAssist Pro é uma plataforma de gestão destinada a
              psicólogos e clínicas. A solução oferece recursos de agenda,
              organização de clientes e sessões, controles financeiros,
              comunicações, relatórios, automações e recursos de inteligência
              artificial, de acordo com o plano contratado.
            </p>

            <p>
              Esta Política se aplica aos dados tratados por meio do site,
              aplicações web, APIs e integrações do PsyAssist Pro.
            </p>

            <div className="psy-legal-callout">
              <Database size={18} />

              <p>
                Dependendo do contexto, o PsyAssist Pro pode atuar como
                controlador dos dados necessários à conta, contratação,
                segurança e operação do serviço e, em relação a dados de
                clientes/pacientes inseridos pelo profissional, pode atuar
                como operador, tratando-os conforme as instruções do
                profissional responsável.
              </p>
            </div>
          </section>

          <section
            id="dados"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              02
            </span>

            <h2>Quais dados podemos tratar</h2>

            <p>
              Os dados efetivamente tratados dependem dos recursos utilizados
              pelo usuário. Eles podem incluir:
            </p>

            <ul>
              <li>
                <strong>Dados de conta e identificação profissional:</strong>{" "}
                nome, e-mail, telefone, CRP, estabelecimento, cidade, estado,
                imagem de perfil e configurações da conta.
              </li>

              <li>
                <strong>Dados de contratação e assinatura:</strong>{" "}
                plano, status da assinatura, identificadores de cobrança e
                informações relacionadas ao ciclo da contratação.
              </li>

              <li>
                <strong>Dados de clientes e atendimentos:</strong>{" "}
                informações cadastradas pelo profissional para organizar
                agenda, sessões, prontuários, anamneses, documentos e demais
                fluxos disponíveis na plataforma.
              </li>

              <li>
                <strong>Dados financeiros:</strong>{" "}
                receitas, despesas, pagamentos, cobranças, recibos e dados
                Pix cadastrados pelo profissional para uso em comunicações.
              </li>

              <li>
                <strong>Dados de comunicação:</strong>{" "}
                histórico técnico de mensagens e e-mails enviados pela
                plataforma, status de entrega e informações necessárias ao
                funcionamento de comunicações por e-mail e WhatsApp.
              </li>

              <li>
                <strong>Dados técnicos e de segurança:</strong>{" "}
                informações de sessão, autenticação, registros de acesso,
                dispositivo e dados necessários à prevenção de abuso e
                manutenção da segurança da conta.
              </li>

              <li>
                <strong>Dados de integrações:</strong>{" "}
                quando o usuário autoriza uma integração, podemos tratar os
                dados estritamente necessários para que ela funcione.
              </li>
            </ul>

            <p>
              O profissional que inserir dados pessoais ou dados pessoais
              sensíveis de seus clientes/pacientes na plataforma é responsável
              por possuir fundamento jurídico adequado para esse tratamento e
              por utilizar o serviço de forma compatível com suas obrigações
              profissionais e legais.
            </p>
          </section>

          <section
            id="finalidades"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              03
            </span>

            <h2>Para que utilizamos os dados</h2>

            <p>
              Utilizamos os dados somente para finalidades compatíveis com a
              operação do PsyAssist Pro, incluindo:
            </p>

            <ul>
              <li>criar, autenticar e manter a conta do usuário;</li>
              <li>
                disponibilizar agenda, gestão de clientes, sessões,
                financeiro, relatórios e demais funcionalidades contratadas;
              </li>
              <li>
                processar assinaturas, controlar acesso aos planos e oferecer
                suporte relacionado à contratação;
              </li>
              <li>
                executar automações solicitadas ou configuradas pelo usuário;
              </li>
              <li>
                enviar comunicações transacionais e operacionais;
              </li>
              <li>
                sincronizar recursos autorizados de terceiros, como Google
                Calendar;
              </li>
              <li>
                disponibilizar recursos da PSYA e de inteligência artificial
                quando o recurso for utilizado;
              </li>
              <li>
                proteger contas, investigar falhas, prevenir fraude e manter a
                disponibilidade e segurança da plataforma;
              </li>
              <li>
                cumprir obrigações legais, regulatórias ou determinações de
                autoridades competentes.
              </li>
            </ul>
          </section>

          <section
            id="google"
            className="psy-legal-section psy-legal-section--google"
          >
            <span className="psy-legal-section__index">
              04
            </span>

            <div className="psy-legal-section__heading-with-icon">
              <span>
                <CalendarDays size={19} />
              </span>

              <h2>Uso de dados do Google Calendar</h2>
            </div>

            <p>
              A integração com o Google Calendar é <strong>opcional</strong> e
              somente é ativada quando o usuário escolhe conectar sua conta
              Google e concede autorização na tela de consentimento do Google.
            </p>

            <p>
              Para disponibilizar a sincronização de agenda, o PsyAssist Pro
              solicita o escopo:
            </p>

            <code className="psy-legal-code">
              https://www.googleapis.com/auth/calendar.events
            </code>

            <p>
              Quando autorizado, esse acesso pode ser utilizado para consultar,
              criar, atualizar e remover eventos do Google Calendar relacionados
              aos agendamentos sincronizados pelo usuário. Quando aplicável a
              uma sessão online, o evento também pode solicitar a criação de
              dados de conferência para gerar um link do Google Meet.
            </p>

            <div className="psy-legal-grid">
              <div>
                <strong>O que podemos acessar</strong>
                <p>
                  Eventos do calendário necessários para realizar e manter a
                  sincronização autorizada pelo usuário.
                </p>
              </div>

              <div>
                <strong>O que podemos armazenar</strong>
                <p>
                  Tokens de autorização necessários à conexão, identificadores
                  da conta/calendário, identificadores de eventos e metadados
                  necessários à sincronização.
                </p>
              </div>

              <div>
                <strong>Para que usamos</strong>
                <p>
                  Exclusivamente para fornecer os recursos de agenda e
                  conferência solicitados pelo próprio usuário.
                </p>
              </div>

              <div>
                <strong>Como desconectar</strong>
                <p>
                  O usuário pode desconectar a integração nas configurações
                  do PsyAssist Pro. Também pode revogar o acesso pela própria
                  Conta Google.
                </p>
              </div>
            </div>

            <div className="psy-legal-callout psy-legal-callout--google">
              <ShieldCheck size={18} />
              <p>
                O uso de informações recebidas das APIs do Google pelo
                PsyAssist Pro observará a Google API Services User Data Policy,
                inclusive os requisitos de Limited Use aplicáveis.
              </p>
            </div>

            <p>
              Os dados do Google não são utilizados para publicidade, venda de
              perfis, criação de audiência publicitária ou qualquer finalidade
              incompatível com a funcionalidade de integração explicitamente
              apresentada ao usuário.
            </p>
          </section>

          <section
            id="compartilhamento"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              05
            </span>

            <h2>
              Compartilhamento e prestadores de serviço
            </h2>

            <p>
              O PsyAssist Pro não vende dados pessoais. Dados podem ser
              processados por prestadores necessários à operação da plataforma,
              sempre de acordo com a finalidade do recurso utilizado.
            </p>

            <p>
              Conforme as funcionalidades utilizadas, esses prestadores podem
              incluir serviços de infraestrutura e hospedagem, banco de dados,
              processamento de pagamentos, e-mail, WhatsApp, inteligência
              artificial e APIs do Google.
            </p>

            <p>
              Atualmente, a arquitetura pode envolver provedores como Railway,
              Vercel, MongoDB, Stripe, Resend, Twilio, OpenAI e Google. Cada
              serviço é utilizado somente nos fluxos em que sua participação é
              necessária.
            </p>

            <p>
              Também poderemos compartilhar informações quando necessário para
              cumprir obrigação legal, ordem judicial ou determinação válida de
              autoridade competente, respeitados os limites aplicáveis.
            </p>
          </section>

          <section
            id="retencao"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              06
            </span>

            <h2>Retenção e exclusão</h2>

            <p>
              Mantemos dados pelo período necessário para fornecer o serviço,
              cumprir finalidades legítimas informadas nesta Política, atender
              obrigações legais e regulatórias, resolver disputas e proteger a
              segurança da plataforma.
            </p>

            <p>
              Quando uma informação deixa de ser necessária, ela pode ser
              excluída ou anonimizada, observadas as hipóteses legais que
              autorizem ou exijam sua conservação.
            </p>

            <p>
              A desconexão do Google Calendar interrompe novas operações de
              sincronização com a conta desconectada. Tokens e dados técnicos
              vinculados à conexão poderão ser revogados, removidos ou
              conservados pelo período estritamente necessário a segurança,
              auditoria e cumprimento de obrigações aplicáveis.
            </p>
          </section>

          <section
            id="seguranca"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              07
            </span>

            <div className="psy-legal-section__heading-with-icon">
              <span>
                <LockKeyhole size={19} />
              </span>

              <h2>Segurança</h2>
            </div>

            <p>
              Adotamos medidas técnicas e organizacionais destinadas a reduzir
              riscos de acesso não autorizado, alteração, divulgação ou perda
              de dados. Entre elas podem estar autenticação, controles de
              acesso, segregação lógica, uso de conexões criptografadas,
              proteção de credenciais e monitoramento técnico.
            </p>

            <p>
              Nenhum ambiente digital pode garantir risco zero. Por isso,
              também recomendamos que usuários mantenham senhas fortes,
              protejam seus dispositivos e não compartilhem credenciais.
            </p>
          </section>

          <section
            id="direitos"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              08
            </span>

            <h2>Direitos dos titulares</h2>

            <p>
              Nos termos da legislação aplicável, inclusive a Lei Geral de
              Proteção de Dados Pessoais — LGPD, o titular pode exercer direitos
              relacionados aos seus dados pessoais, conforme o caso e os limites
              legais aplicáveis.
            </p>

            <p>
              Esses direitos podem incluir confirmação da existência de
              tratamento, acesso, correção, anonimização, bloqueio ou eliminação
              quando cabível, portabilidade nos termos da regulamentação,
              informações sobre compartilhamento, revogação de consentimento e
              outras medidas previstas em lei.
            </p>

            <p>
              Quando o dado estiver sendo tratado pelo PsyAssist Pro em nome de
              um psicólogo ou clínica, algumas solicitações poderão precisar ser
              encaminhadas ao profissional ou organização que atua como
              controlador daquele tratamento.
            </p>
          </section>

          <section
            id="contato"
            className="psy-legal-section"
          >
            <span className="psy-legal-section__index">
              09
            </span>

            <h2>Contato sobre privacidade</h2>

            <p>
              Para solicitações relacionadas a esta Política, privacidade ou
              proteção de dados, utilize:
            </p>

            <a
              className="psy-legal-contact"
              href="mailto:stardevs09@gmail.com"
            >
              <Mail size={18} />

              <span>
                <small>Contato de privacidade</small>
                <strong>stardevs09@gmail.com</strong>
              </span>

              <ExternalLink size={15} />
            </a>

            <p>
              Para proteger os titulares, poderemos solicitar informações
              adicionais para confirmar identidade e legitimidade antes de
              atender determinadas solicitações.
            </p>
          </section>

          <section className="psy-legal-section">
            <span className="psy-legal-section__index">
              10
            </span>

            <h2>Atualizações desta Política</h2>

            <p>
              Esta Política pode ser atualizada para refletir mudanças na
              plataforma, na legislação ou em nossas práticas de tratamento.
              Quando houver alteração relevante, a versão e a data de
              atualização serão modificadas nesta página e, quando necessário,
              será apresentado aviso adicional ao usuário.
            </p>
          </section>

          <footer className="psy-legal-document-footer">
            <div>
              <Sparkles size={17} />
              <span>
                <strong>PsyAssist Pro</strong>
                <small>
                  Privacidade como parte do produto.
                </small>
              </span>
            </div>

            <div className="psy-legal-document-footer__links">
              <a href="/termos-de-uso">
                Termos de Uso
              </a>
              <a href="/">
                Página inicial
              </a>
            </div>
          </footer>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
